import React, { useRef, useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { ApplicationStore, CropConfig } from '../../../../store/ApplicationStore';
import { CompositionStateStore } from '../../../../store/CompositionStateStore';
import { WindowStore } from '../../../../store/WindowStore';
import { CompositionService } from '../../../../services/CompositionService';
import { ScriptLayerClip } from '../../../../../engine/clips/script/script';
import { COMPOSITION_CONTAINER_ID } from '../../../../config/config';
import './CropContainer.styles.scss';

interface CropContainerProps {
    onApplyCrop?: (crop: CropConfig, scope: 'current' | 'all') => void;
    onDoneCrop?: (crop: CropConfig, scope: 'current' | 'all') => boolean | void;
}

type ScriptSceneVisualSnapshot = {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    crop?: { x: number; y: number; width: number; height: number };
};

type OriginalCropState =
    | {
          kind: 'script';
          clipId: string;
          sourceIndex: number;
          sceneIndex: number;
          globalCrop?: { x: number; y: number; width: number; height: number };
          sceneVisual?: ScriptSceneVisualSnapshot;
      }
    | {
          kind: 'clip';
          clipId: string;
          crop?: { x: number; y: number; width: number; height: number };
      };

const CropContainer: React.FC<CropContainerProps> = observer(({ onApplyCrop, onDoneCrop }) => {
    const isCropMode = ApplicationStore.isCropMode;
    const selectedVisualElement = ApplicationStore.selectedVisualElement;
    const { workingCrop, cropScope, cropZoom } = ApplicationStore;
    
    // Drag/resize state
    const [isDragging, setIsDragging] = useState(false);
    const [resizeHandle, setResizeHandle] = useState<string | null>(null);
    const dragStartRef = useRef({ mouseX: 0, mouseY: 0, crop: { x: 0, y: 0, width: 1, height: 1 } });
    const clipScreenRef = useRef({ x: 0, y: 0, w: 0, h: 0 });
    
    // Store original crop(s) so we can restore on cancel / escape.
    const originalCropRef = useRef<OriginalCropState | null>(null);
    
    // Force re-render after view updates
    const [viewReady, setViewReady] = useState(false);
    
    // Store calculated bounds in state to control update timing
    const [clipBoundsState, setClipBoundsState] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

    const isFullCrop = (crop: { x: number; y: number; width: number; height: number } | undefined): boolean => {
        if (!crop) return true;
        const x = Number(crop.x ?? 0);
        const y = Number(crop.y ?? 0);
        const w = Number(crop.width ?? 1);
        const h = Number(crop.height ?? 1);
        return Math.abs(x) < 1e-6 && Math.abs(y) < 1e-6 && Math.abs(w - 1) < 1e-6 && Math.abs(h - 1) < 1e-6;
    };

    const getCurrentAspectPreset = (): 'horizontal' | 'vertical' | 'square' => {
        const comp: any = CompositionService.composition;
        const cw = Number(comp?.settings?.width ?? comp?.width ?? 1920);
        const ch = Number(comp?.settings?.height ?? comp?.height ?? 1080);
        const ratio = ch > 0 ? cw / ch : 16 / 9;
        if (Math.abs(ratio - 1) < 0.06) return 'square';
        return ratio < 1 ? 'vertical' : 'horizontal';
    };

    const getScriptSceneVisualOverride = useCallback(
        (clip: ScriptLayerClip, sceneIndex: number, sourceIndex: number): ScriptSceneVisualSnapshot | undefined => {
            try {
                const preset = getCurrentAspectPreset();
                const sceneProps: any = clip.getSceneProperties(sceneIndex);
                const byAspect = (sceneProps as any)?.visualOverridesByAspect;
                const map =
                    byAspect && (byAspect as any)[preset] && typeof (byAspect as any)[preset].get === 'function'
                        ? (byAspect as any)[preset]
                        : preset === 'horizontal'
                          ? (sceneProps as any)?.visualOverrides
                          : undefined;
                const ov = map?.get?.(sourceIndex);
                if (!ov || typeof ov !== 'object') return undefined;

                const readNum = (v: any): number | undefined =>
                    typeof v === 'number' && Number.isFinite(v) ? Number(v) : undefined;

                const out: any = {
                    x: readNum((ov as any).x),
                    y: readNum((ov as any).y),
                    width: readNum((ov as any).width),
                    height: readNum((ov as any).height),
                };
                if (Object.prototype.hasOwnProperty.call(ov as any, 'crop')) {
                    const c = (ov as any).crop;
                    if (c && typeof c === 'object') {
                        const x = Number((c as any).x);
                        const y = Number((c as any).y);
                        const width = Number((c as any).width);
                        const height = Number((c as any).height);
                        if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                            out.crop = { x, y, width, height };
                        } else {
                            out.crop = undefined;
                        }
                    } else {
                        out.crop = undefined;
                    }
                }
                return out;
            } catch {
                return undefined;
            }
        },
        []
    );

    const restoreOriginalState = useCallback(
        (opts?: { restoreGlobalCrop?: boolean; restoreSceneCrop?: boolean }) => {
            const original = originalCropRef.current;
            if (!original) return;

            const clip = CompositionService.getClipById(original.clipId);
            if (original.kind === 'script' && clip instanceof ScriptLayerClip) {
                const restoreGlobalCrop = opts?.restoreGlobalCrop ?? true;
                const restoreSceneCrop = opts?.restoreSceneCrop ?? true;

                if (restoreGlobalCrop) {
                    try { clip.setVideoSourceCrop(original.sourceIndex, original.globalCrop); } catch {}
                }

                const sceneVisual = original.sceneVisual;
                const restoreSceneProps: any = {
                    x: sceneVisual?.x,
                    y: sceneVisual?.y,
                    width: sceneVisual?.width,
                    height: sceneVisual?.height,
                };
                if (restoreSceneCrop) {
                    restoreSceneProps.crop = sceneVisual?.crop;
                }
                try { clip.setSceneVisualProperties(original.sceneIndex, original.sourceIndex, restoreSceneProps); } catch {}
                try { CompositionService.notifyClipUpdated(clip); } catch {}
                return;
            }

            if (original.kind === 'clip' && clip && 'setCrop' in clip && typeof (clip as any).setCrop === 'function') {
                (clip as any).setCrop(original.crop);
                try { CompositionService.notifyClipUpdated(clip); } catch {}
            }
        },
        []
    );
    
    // Exit crop mode when visual element is deselected (treat as cancel)
    useEffect(() => {
        if (isCropMode && !selectedVisualElement) {
            // Treat deselect as cancel: restore original crop(s) if we have them.
            restoreOriginalState();
            originalCropRef.current = null;
            ApplicationStore.exitCropMode();
        }
    }, [isCropMode, restoreOriginalState, selectedVisualElement]);
    
    // Reset state when exiting crop mode
    useEffect(() => {
        if (!isCropMode) {
            setViewReady(false);
            setClipBoundsState(null);
        }
    }, [isCropMode]);

    // Temporarily show full source when entering crop mode (so user can see what to expand into)
    useEffect(() => {
        if (!isCropMode || !selectedVisualElement) return;
        
        const clip = CompositionService.getClipById(selectedVisualElement.clipId);
        if (!clip) return;

        // Reset view ready state
        setViewReady(false);

        // Store current crop(s) and temporarily reset to show full source.
        if (clip instanceof ScriptLayerClip) {
            const sourceIndex = selectedVisualElement.sourceIndex ?? 0;
            const absCompTime = Number(CompositionService.composition?.currentTime?.seconds ?? 0);
            const clipLocalTime = clip.mapCompTimeToClipLocal(absCompTime);
            const sceneIndex = clipLocalTime != null ? clip.findSceneIndexForTime(clipLocalTime) : 0;

            const globalCrop = clip.getVideoSourceVisual(sourceIndex)?.crop;
            const sceneVisual = getScriptSceneVisualOverride(clip, sceneIndex, sourceIndex);
            const effectiveVisual = clip.getSceneVisualProperties(sceneIndex, sourceIndex);
            const effectiveCrop = effectiveVisual?.crop ?? globalCrop;

            let previewVisual: { x: number; y: number; width: number; height: number } | undefined;
            try {
                const ex = Number(effectiveVisual?.x ?? NaN);
                const ey = Number(effectiveVisual?.y ?? NaN);
                const ew = Number(effectiveVisual?.width ?? NaN);
                const eh = Number(effectiveVisual?.height ?? NaN);
                if ([ex, ey, ew, eh].every(Number.isFinite) && ew > 0 && eh > 0) {
                    if (isFullCrop(effectiveCrop)) {
                        previewVisual = { x: ex, y: ey, width: ew, height: eh };
                    } else {
                        const c = effectiveCrop as { x: number; y: number; width: number; height: number };
                        const cw = Number(c.width);
                        const ch = Number(c.height);
                        if (cw > 1e-6 && ch > 1e-6) {
                            const baseW = ew / cw;
                            const baseH = eh / ch;
                            const cx = Number(c.x ?? 0) + cw / 2;
                            const cy = Number(c.y ?? 0) + ch / 2;
                            previewVisual = {
                                x: ex - (cx - 0.5) * baseW,
                                y: ey - (cy - 0.5) * baseH,
                                width: baseW,
                                height: baseH,
                            };
                        }
                    }
                }
            } catch {}

            originalCropRef.current = {
                kind: 'script',
                clipId: selectedVisualElement.clipId,
                sourceIndex,
                sceneIndex,
                globalCrop,
                sceneVisual,
            };

            // Temporarily reset to show full source in the current scene/layout context.
            try { clip.setVideoSourceCrop(sourceIndex, undefined); } catch {}
            const previewSceneProps: any = { crop: undefined };
            if (previewVisual) {
                previewSceneProps.x = previewVisual.x;
                previewSceneProps.y = previewVisual.y;
                previewSceneProps.width = previewVisual.width;
                previewSceneProps.height = previewVisual.height;
            }
            try { clip.setSceneVisualProperties(sceneIndex, sourceIndex, previewSceneProps); } catch {}
        } else if ('getCrop' in clip && typeof (clip as any).getCrop === 'function') {
            const currentCrop = (clip as any).getCrop();
            originalCropRef.current = {
                kind: 'clip',
                clipId: selectedVisualElement.clipId,
                crop: currentCrop,
            };
            // Temporarily reset clip to show full source.
            if ('setCrop' in clip && typeof (clip as any).setCrop === 'function') {
                (clip as any).setCrop(undefined);
            }
        } else {
            originalCropRef.current = null;
        }

        if (originalCropRef.current) {
            
            // Force a seek to current time to re-decode the frame at full resolution
            const currentTime = CompositionService.composition.currentTime;
            // Helper to calculate bounds after view update
            const updateBoundsAndShow = () => {
                // Get fresh clip/view references
                const freshClip = CompositionService.getClipById(selectedVisualElement.clipId);
                let freshView: any = null;
                if (freshClip instanceof ScriptLayerClip && selectedVisualElement?.sourceIndex !== undefined) {
                    freshView = freshClip.getVideoSprite(selectedVisualElement.sourceIndex);
                } else if (freshClip) {
                    freshView = (freshClip as any).view;
                }
                
                // Get view bounds
                let viewBounds = { x: 0, y: 0, width: 1920, height: 1080 };
                if (freshView && typeof freshView.getBounds === 'function') {
                    const bounds = freshView.getBounds();
                    viewBounds = { x: bounds.x, y: bounds.y, width: bounds.width, height: bounds.height };
                }
                
                setClipBoundsState(viewBounds);
                setViewReady(true);
            };

            // Seek forces the video to decode a fresh frame with the new (no crop) settings
            CompositionService.seek(currentTime.seconds).then(() => {
                // After seek completes, compute frame
                CompositionService.composition.computeFrame();
                // Wait for Pixi to render (one frame), then compute again to be sure
                requestAnimationFrame(() => {
                    CompositionService.composition.computeFrame();
                    // Wait another frame for Pixi to update bounds
                    requestAnimationFrame(() => {
                        updateBoundsAndShow();
                    });
                });
            });
        } else {
            // Still need to update bounds for first-time crop
            setClipBoundsState(null); // Will be calculated at render time
            setViewReady(true);
        }
        
        // Cleanup: restore original crop if cancelled (escape/deselect)
        return () => {
            // Note: cleanup runs when exiting crop mode
            // If Done was clicked, the new crop is already applied, so this won't matter
            // If cancelled, we should restore - but this cleanup might run after Done too
            // We'll handle this in handleDone to not restore after applying
        };
    }, [getScriptSceneVisualOverride, isCropMode, selectedVisualElement]);

    // Handle Escape key to cancel crop mode
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && ApplicationStore.isCropMode) {
                // Restore original crop if any
                restoreOriginalState();
                originalCropRef.current = null;
                ApplicationStore.exitCropMode();
                setTimeout(() => { CompositionService.recomputeFrame(); }, 100);
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [restoreOriginalState, selectedVisualElement]);

    // Handle mouse move for resize/drag
    useEffect(() => {
        if (!isDragging && !resizeHandle) return;

        const handleMouseMove = (e: MouseEvent) => {
            const { w: clipW, h: clipH } = clipScreenRef.current;
            const startCrop = dragStartRef.current.crop;
            const dx = (e.clientX - dragStartRef.current.mouseX) / clipW;
            const dy = (e.clientY - dragStartRef.current.mouseY) / clipH;
            
            if (isDragging) {
                // Dragging the crop rectangle
                let newX = Math.max(0, Math.min(1 - startCrop.width, startCrop.x + dx));
                let newY = Math.max(0, Math.min(1 - startCrop.height, startCrop.y + dy));
                ApplicationStore.setWorkingCrop({ ...workingCrop, x: newX, y: newY });
            } else if (resizeHandle) {
                // Resizing
                let newCrop = { ...startCrop };
                const minSize = 0.05;

                if (resizeHandle.includes('w')) {
                    const newX = Math.max(0, Math.min(startCrop.x + startCrop.width - minSize, startCrop.x + dx));
                    newCrop.width = startCrop.width - (newX - startCrop.x);
                    newCrop.x = newX;
                }
                if (resizeHandle.includes('e')) {
                    newCrop.width = Math.max(minSize, Math.min(1 - startCrop.x, startCrop.width + dx));
                }
                if (resizeHandle.includes('n')) {
                    const newY = Math.max(0, Math.min(startCrop.y + startCrop.height - minSize, startCrop.y + dy));
                    newCrop.height = startCrop.height - (newY - startCrop.y);
                    newCrop.y = newY;
                }
                if (resizeHandle.includes('s')) {
                    newCrop.height = Math.max(minSize, Math.min(1 - startCrop.y, startCrop.height + dy));
                }

                ApplicationStore.setWorkingCrop(newCrop);
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            setResizeHandle(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, resizeHandle, workingCrop]);

    // Don't render if not in crop mode
    if (!isCropMode) {
        return null;
    }
    
    // Show loading state while waiting for view to update
    if (!viewReady) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.7)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
            }}>
                <div>Loading crop view...</div>
            </div>
        );
    }

    // Get clip and its view
    const clip = selectedVisualElement ? CompositionService.getClipById(selectedVisualElement.clipId) : null;
    let view: any = null;
    
    if (clip instanceof ScriptLayerClip && selectedVisualElement?.sourceIndex !== undefined) {
        view = clip.getVideoSprite(selectedVisualElement.sourceIndex);
    } else if (clip) {
        view = (clip as any).view;
    }

    // Use stored bounds if available (updated after seek completes), otherwise calculate
    let clipBounds: { x: number; y: number; width: number; height: number };
    
    if (clipBoundsState) {
        clipBounds = clipBoundsState;
    } else {
        // Use current preview bounds from the rendered view.
        let viewBounds = { x: 0, y: 0, width: 1920, height: 1080 };
        if (view && typeof view.getBounds === 'function') {
            const bounds = view.getBounds();
            viewBounds = { x: bounds.x, y: bounds.y, width: bounds.width, height: bounds.height };
        } else {
            // Fallback to display dimensions
            viewBounds = { x: 0, y: 0, width: CompositionStateStore.display.width, height: CompositionStateStore.display.height };
        }
        clipBounds = viewBounds;
    }

    // Find canvas and calculate positions
    const compositionContainer = document.getElementById(COMPOSITION_CONTAINER_ID);
    const canvasEl = compositionContainer?.querySelector('canvas') as HTMLCanvasElement | null;
    
    if (!canvasEl) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
            }}>
                <div style={{ background: '#333', padding: '20px', borderRadius: '8px' }}>
                    No canvas found. Press Escape to exit.
                </div>
            </div>
        );
    }

    // Get canvas rect and calculate pixel ratio
    const canvasRect = canvasEl.getBoundingClientRect();
    const [canvasResW, canvasResH] = WindowStore.canvasResolution;
    const { width: displayW, height: displayH } = CompositionStateStore.display;
    const pixelRatio = Math.min(canvasResW / displayW, canvasResH / displayH) || 1;

    // Convert clip bounds to screen coordinates
    const clipScreenX = canvasRect.left + clipBounds.x * pixelRatio;
    const clipScreenY = canvasRect.top + clipBounds.y * pixelRatio;
    const clipScreenW = clipBounds.width * pixelRatio;
    const clipScreenH = clipBounds.height * pixelRatio;
    
    // Store for use in mouse handlers
    clipScreenRef.current = { x: clipScreenX, y: clipScreenY, w: clipScreenW, h: clipScreenH };
    
    // Crop rectangle in screen coordinates
    const cropScreenX = clipScreenX + workingCrop.x * clipScreenW;
    const cropScreenY = clipScreenY + workingCrop.y * clipScreenH;
    const cropScreenW = workingCrop.width * clipScreenW;
    const cropScreenH = workingCrop.height * clipScreenH;

    // Start dragging the crop rectangle
    const handleCropMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        dragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            crop: { ...workingCrop }
        };
    };

    // Start resizing from a handle
    const handleResizeMouseDown = (handle: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setResizeHandle(handle);
        dragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            crop: { ...workingCrop }
        };
    };

    const handleReset = () => {
        ApplicationStore.resetCrop();
    };

    const handleDone = () => {
        const original = originalCropRef.current;
        const handledByExternalDone = Boolean(onDoneCrop?.(workingCrop, cropScope));

        if (handledByExternalDone) {
            // External handlers may consume "Done" (for capture-only workflows).
            // Restore full original state since no edit is being committed to the clip.
            restoreOriginalState();
            originalCropRef.current = null;
            ApplicationStore.exitCropMode();
            setTimeout(() => { CompositionService.recomputeFrame(); }, 100);
            return;
        }

        onApplyCrop?.(workingCrop, cropScope);

        // Restore temporary preview transform state while keeping the newly applied crop edit.
        if (original?.kind === 'script') {
            restoreOriginalState({
                restoreGlobalCrop: cropScope === 'current',
                restoreSceneCrop: false,
            });
        }

        originalCropRef.current = null;
        ApplicationStore.exitCropMode();
        setTimeout(() => { CompositionService.recomputeFrame(); }, 100);
    };

    const handleCancel = () => {
        // Restore original crop
        restoreOriginalState();
        originalCropRef.current = null;
        ApplicationStore.exitCropMode();
        setTimeout(() => { CompositionService.recomputeFrame(); }, 100);
    };

    const zoomPercent = Math.round(cropZoom * 100);

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            pointerEvents: 'none'
        }}>
            {/* Dark overlay mask - top */}
            <div style={{
                position: 'fixed',
                left: clipScreenX,
                top: clipScreenY,
                width: clipScreenW,
                height: Math.max(0, cropScreenY - clipScreenY),
                background: 'rgba(0, 0, 0, 0.7)',
                pointerEvents: 'all'
            }} />
            
            {/* Dark overlay mask - bottom */}
            <div style={{
                position: 'fixed',
                left: clipScreenX,
                top: cropScreenY + cropScreenH,
                width: clipScreenW,
                height: Math.max(0, (clipScreenY + clipScreenH) - (cropScreenY + cropScreenH)),
                background: 'rgba(0, 0, 0, 0.7)',
                pointerEvents: 'all'
            }} />
            
            {/* Dark overlay mask - left */}
            <div style={{
                position: 'fixed',
                left: clipScreenX,
                top: cropScreenY,
                width: Math.max(0, cropScreenX - clipScreenX),
                height: cropScreenH,
                background: 'rgba(0, 0, 0, 0.7)',
                pointerEvents: 'all'
            }} />
            
            {/* Dark overlay mask - right */}
            <div style={{
                position: 'fixed',
                left: cropScreenX + cropScreenW,
                top: cropScreenY,
                width: Math.max(0, (clipScreenX + clipScreenW) - (cropScreenX + cropScreenW)),
                height: cropScreenH,
                background: 'rgba(0, 0, 0, 0.7)',
                pointerEvents: 'all'
            }} />

            {/* Original clip border (white dashed) */}
            <div style={{
                position: 'fixed',
                left: clipScreenX,
                top: clipScreenY,
                width: clipScreenW,
                height: clipScreenH,
                border: '2px dashed rgba(255, 255, 255, 0.6)',
                boxSizing: 'border-box',
                pointerEvents: 'none'
            }} />

            {/* Crop rectangle */}
            <div 
                style={{
                    position: 'fixed',
                    left: cropScreenX,
                    top: cropScreenY,
                    width: cropScreenW,
                    height: cropScreenH,
                    border: '2px dashed #fff',
                    boxSizing: 'border-box',
                    pointerEvents: 'all',
                    cursor: isDragging ? 'grabbing' : 'grab'
                }}
                onMouseDown={handleCropMouseDown}
            >
                {/* Grid lines */}
                <div style={{ position: 'absolute', left: 0, right: 0, top: '33.33%', height: '1px', background: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', left: 0, right: 0, top: '66.66%', height: '1px', background: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: '33.33%', width: '1px', background: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: '66.66%', width: '1px', background: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />

                {/* Corner handles */}
                {['nw', 'ne', 'sw', 'se'].map(corner => (
                    <div 
                        key={corner} 
                        style={{
                            position: 'absolute',
                            width: 12,
                            height: 12,
                            background: '#fff',
                            border: '1px solid #333',
                            borderRadius: 2,
                            ...(corner.includes('n') ? { top: -6 } : { bottom: -6 }),
                            ...(corner.includes('w') ? { left: -6 } : { right: -6 }),
                            cursor: `${corner}-resize`,
                            pointerEvents: 'all'
                        }}
                        onMouseDown={handleResizeMouseDown(corner)}
                    />
                ))}
                
                {/* Edge handles */}
                <div 
                    style={{ position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)', width: 12, height: 12, background: '#fff', border: '1px solid #333', borderRadius: 2, cursor: 'n-resize', pointerEvents: 'all' }}
                    onMouseDown={handleResizeMouseDown('n')}
                />
                <div 
                    style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', width: 12, height: 12, background: '#fff', border: '1px solid #333', borderRadius: 2, cursor: 's-resize', pointerEvents: 'all' }}
                    onMouseDown={handleResizeMouseDown('s')}
                />
                <div 
                    style={{ position: 'absolute', left: -6, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, background: '#fff', border: '1px solid #333', borderRadius: 2, cursor: 'w-resize', pointerEvents: 'all' }}
                    onMouseDown={handleResizeMouseDown('w')}
                />
                <div 
                    style={{ position: 'absolute', right: -6, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, background: '#fff', border: '1px solid #333', borderRadius: 2, cursor: 'e-resize', pointerEvents: 'all' }}
                    onMouseDown={handleResizeMouseDown('e')}
                />
            </div>

            {/* Toolbar */}
            <div style={{
                position: 'fixed',
                left: clipScreenX + clipScreenW / 2,
                top: Math.max(10, clipScreenY - 50),
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                pointerEvents: 'all'
            }}>
                <button 
                    onClick={handleReset}
                    style={{ padding: '6px 16px', border: 'none', borderRadius: 4, background: 'transparent', cursor: 'pointer' }}
                >
                    Reset
                </button>
                <button 
                    onClick={handleCancel}
                    style={{ padding: '6px 16px', border: 'none', borderRadius: 4, background: '#666', color: '#fff', cursor: 'pointer' }}
                >
                    Cancel
                </button>
                <span style={{ fontSize: 14, color: '#666', minWidth: 48, textAlign: 'center' }}>{zoomPercent}%</span>
                <button 
                    onClick={handleDone}
                    style={{ padding: '6px 16px', border: 'none', borderRadius: 4, background: '#1a1a1a', color: '#fff', cursor: 'pointer' }}
                >
                    Done
                </button>
            </div>

            {/* Scope toggle */}
            <div style={{
                position: 'fixed',
                left: clipScreenX + clipScreenW / 2,
                top: clipScreenY + clipScreenH + 20,
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                pointerEvents: 'all'
            }}>
                <span style={{ fontSize: 14, color: '#666' }}>Selected in</span>
                <div style={{ display: 'flex', background: '#f5f5f5', borderRadius: 6, padding: 4, gap: 4 }}>
                    <button 
                        onClick={() => ApplicationStore.setCropScope('current')}
                        style={{ 
                            padding: '6px 14px', 
                            border: 'none', 
                            borderRadius: 4, 
                            background: cropScope === 'current' ? '#fff' : 'transparent',
                            boxShadow: cropScope === 'current' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer',
                            fontSize: 13
                        }}
                    >
                        Current scene
                    </button>
                    <button 
                        onClick={() => ApplicationStore.setCropScope('all')}
                        style={{ 
                            padding: '6px 14px', 
                            border: 'none', 
                            borderRadius: 4, 
                            background: cropScope === 'all' ? '#fff' : 'transparent',
                            boxShadow: cropScope === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer',
                            fontSize: 13
                        }}
                    >
                        All scenes
                    </button>
                </div>
            </div>
        </div>
    );
});

export default CropContainer;
